import React, { useState, useEffect } from 'react';
import { 
  Typography, Grid, Paper, Box, Button, 
  TableContainer, Table, TableHead, TableBody, TableRow, TableCell,
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
  TextField, FormControl, InputLabel, Select, MenuItem,
  Chip, IconButton, Tooltip, LinearProgress, CircularProgress,
  Divider, Accordion, AccordionSummary, AccordionDetails,
  FormControlLabel, Checkbox, Stack, Card, CardContent,
  useTheme, alpha, Container
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { 
  Add, Edit, Delete, Print, 
  ExpandMore, Refresh,
  Assignment as AssignmentIcon,
  OpenInNew, Visibility, Close,
  Science as ScienceIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { supabase } from '../../../../supabaseClient';
import { usePermissions } from '../../../../utils/permissions';

const CertificateOfAnalysis = ({ 
  formulation, 
  showSnackbar, 
  user, 
  tests, 
  setTests, 
  loading, 
  setLoading,
  fetchTests,
  customers,
  brands,
  products,
  canEdit: propCanEdit,
  canDelete: propCanDelete
}) => {
  const theme = useTheme();
  const { canAccessPage, canEditPage, hasPermission, isManagerOrAbove, userRole, hierarchyLevel } = usePermissions();
  
  // Accordion state management with localStorage
  const getStorageKey = (accordionType) => `testsTab_${formulation?.id}_${accordionType}_expanded`;
  
  const [coaExpanded, setCoaExpanded] = useState(() => {
    const stored = localStorage.getItem(getStorageKey('coa'));
    return stored ? JSON.parse(stored) : false;
  });

  // Save accordion states to localStorage
  const handleCoaExpandChange = (event, isExpanded) => {
    setCoaExpanded(isExpanded);
    if (formulation?.id) {
      localStorage.setItem(getStorageKey('coa'), JSON.stringify(isExpanded));
    }
  };

  const [coaDialogOpen, setCoaDialogOpen] = useState(false);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [currentTest, setCurrentTest] = useState(null);
  const [brandSearchQuery, setBrandSearchQuery] = useState('');
  const [brandSelectionDialogOpen, setBrandSelectionDialogOpen] = useState(false);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerSelectionDialogOpen, setCustomerSelectionDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewTest, setViewTest] = useState(null);
  
  // Certificate of Analysis form data with all new fields
  const [coaFormData, setCoaFormData] = useState({
    batch_number: '',
    brand_name: '',
    product_name: '',
    reference_number: '',
    customer_id: '',
    customer_name: '',
    customer_address: '',
    customer_contact: '',
    customer_email: '',
    customer_phone: '',
    // Physical Testing
    appearance: '',
    color: '',
    odor: '',
    ph: '',
    specific_gravity: '',
    viscosity: '',
    density: '',
    melting_point: '',
    boiling_point: '',
    refractive_index: '',
    // Chemical Testing
    active_content: '',
    active_content_method: 'HPLC/GC',
    water_content: '',
    water_content_method: 'Karl Fischer',
    ash_content: '',
    ash_content_method: 'Gravimetric',
    heavy_metals: '',
    heavy_metals_method: 'ICP-MS',
    preservative_content: '',
    preservative_content_method: 'HPLC',
    // Microbiological Testing
    total_aerobic_count: '',
    total_aerobic_count_limit: '≤1000 CFU/g',
    yeast_mold_count: '',
    yeast_mold_count_limit: '≤100 CFU/g',
    escherichia_coli: '',
    escherichia_coli_limit: 'Not Detected',
    staphylococcus_aureus: '',
    staphylococcus_aureus_limit: 'Not Detected',
    pseudomonas_aeruginosa: '',
    pseudomonas_aeruginosa_limit: 'Not Detected',
    candida_albicans: '',
    candida_albicans_limit: 'Not Detected',
    // Manufacturing Info
    manufacturing_date: new Date().toISOString().split('T')[0],
    expiry_date: '',
    manufacturing_location: '',
    lot_size: formulation?.target_batch_size?.toString() || '',
    packaging_type: '',
    storage_conditions: 'Store in a cool, dry place',
    // Quality Control
    qc_manager: '',
    qc_technician: '',
    test_method: 'In-house methods / USP / ASTM',
    equipment_used: '',
    test_temperature: '25°C',
    test_humidity: '45% RH',
    // Regulatory & Compliance
    regulatory_status: 'compliant',
    compliance_standards: [],
    allergen_info: '',
    gmo_status: 'non-gmo',
    halal_status: '',
    kosher_status: '',
    organic_status: '',
    cruelty_free: false,
    vegan_status: false,
    // Additional Info
    ingredient_disclosure: '',
    safety_warnings: '',
    shelf_life: '24 months',
    country_of_origin: '',
    certificate_validity_period: '2 years',
    retesting_date: '',
    additional_notes: '',
    issued_date: new Date().toISOString().split('T')[0],
    coa_status: 'draft',
    signature_data: null
  });

  // Delete confirmation states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmStep, setDeleteConfirmStep] = useState(1);
  const [testToDelete, setTestToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Update accordion state when formulation changes
  useEffect(() => {
    if (formulation?.id) {
      const coaStored = localStorage.getItem(getStorageKey('coa'));
      setCoaExpanded(coaStored ? JSON.parse(coaStored) : false);
    }
  }, [formulation?.id]);

  // Use passed permissions or check via usePermissions for tests tab specifically
  const canEdit = propCanEdit !== undefined ? propCanEdit : hasPermission('products.formulations.tests.edit', 'write');
  const canDelete = propCanDelete !== undefined ? propCanDelete : hasPermission('products.formulations.tests.delete', 'delete');
  const canView = hasPermission('products.formulations.tests.view', 'read');

  // Generate a unique reference number
  const generateReferenceNumber = () => {
    const today = new Date();
    const year = today.getFullYear().toString().slice(-2);
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    
    const formulationCode = formulation?.code ? formulation.code.replace(/\s+/g, '-') : '';
    
    const baseRef = formulationCode 
      ? `COA-${formulationCode}-${year}${month}${day}`
      : `COA-${year}${month}${day}`;
    
    let highestSequence = 0;
    
    tests.forEach(test => {
      if (test.reference_number && test.reference_number.startsWith(baseRef)) {
        const sequencePart = test.reference_number.split('-').pop();
        if (sequencePart && !isNaN(parseInt(sequencePart))) {
          const sequence = parseInt(sequencePart);
          if (sequence > highestSequence) {
            highestSequence = sequence;
          }
        }
      }
    });
    
    const nextSequence = highestSequence + 1;
    const sequentialNum = String(nextSequence).padStart(3, '0');
    
    return `${baseRef}-${sequentialNum}`;
  };

  // Open Certificate of Analysis dialog
  const handleCoADialogOpen = (test = null) => {
    if (test) {
      setCurrentTest(test);
      
      setCoaFormData({
        ...test,
        batch_number: test.batch_number || formulation?.batch_number || '',
        compliance_standards: test.compliance_standards || [],
        manufacturing_date: test.manufacturing_date || new Date().toISOString().split('T')[0],
        expiry_date: test.expiry_date || '',
        issued_date: test.issued_date || new Date().toISOString().split('T')[0],
        coa_status: test.coa_status || 'draft',
        signature_data: test.signature_data || null
      });
    } else {
      setCurrentTest(null);
      
      const product = products?.find(p => p.id === formulation?.product_id);
      
      setCoaFormData({
        batch_number: formulation?.batch_number || '',
        brand_name: '',
        product_name: product?.name || '',
        reference_number: generateReferenceNumber(),
        customer_id: '',
        customer_name: '',
        customer_address: '',
        customer_contact: '',
        customer_email: '',
        customer_phone: '',
        // Physical Testing
        appearance: formulation?.appearance || '',
        color: '',
        odor: '',
        ph: formulation?.ph_target || '',
        specific_gravity: '',
        viscosity: formulation?.viscosity_target || '',
        density: '',
        melting_point: '',
        boiling_point: '',
        refractive_index: '',
        // Chemical Testing
        active_content: '',
        active_content_method: 'HPLC/GC',
        water_content: '',
        water_content_method: 'Karl Fischer',
        ash_content: '',
        ash_content_method: 'Gravimetric',
        heavy_metals: 'Compliant',
        heavy_metals_method: 'ICP-MS',
        preservative_content: '',
        preservative_content_method: 'HPLC',
        // Microbiological Testing
        total_aerobic_count: '',
        total_aerobic_count_limit: '≤1000 CFU/g',
        yeast_mold_count: '',
        yeast_mold_count_limit: '≤100 CFU/g',
        escherichia_coli: 'Not Detected',
        escherichia_coli_limit: 'Not Detected',
        staphylococcus_aureus: 'Not Detected',
        staphylococcus_aureus_limit: 'Not Detected',
        pseudomonas_aeruginosa: 'Not Detected',
        pseudomonas_aeruginosa_limit: 'Not Detected',
        candida_albicans: 'Not Detected',
        candida_albicans_limit: 'Not Detected',
        // Manufacturing Info
        manufacturing_date: new Date().toISOString().split('T')[0],
        expiry_date: '',
        manufacturing_location: '',
        lot_size: formulation?.target_batch_size?.toString() || '',
        packaging_type: '',
        storage_conditions: 'Store in a cool, dry place',
        // Quality Control
        qc_manager: '',
        qc_technician: '',
        test_method: 'In-house methods / USP / ASTM',
        equipment_used: '',
        test_temperature: '25°C',
        test_humidity: '45% RH',
        // Regulatory & Compliance
        regulatory_status: 'compliant',
        compliance_standards: [],
        allergen_info: '',
        gmo_status: 'non-gmo',
        halal_status: '',
        kosher_status: '',
        organic_status: '',
        cruelty_free: false,
        vegan_status: false,
        // Additional Info
        ingredient_disclosure: '',
        safety_warnings: '',
        shelf_life: '24 months',
        country_of_origin: '',
        certificate_validity_period: '2 years',
        retesting_date: '',
        additional_notes: '',
        issued_date: new Date().toISOString().split('T')[0],
        coa_status: 'draft',
        signature_data: null
      });
    }
    
    setCoaDialogOpen(true);
  };

  // Handle CoA form field changes
  const handleCoAChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCoaFormData({
      ...coaFormData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Handle customer selection
  const handleCustomerChange = (e) => {
    const customerId = e.target.value;
    const selectedCustomer = customers.find(c => c.id === customerId);
    
    if (selectedCustomer) {
      setCoaFormData({
        ...coaFormData,
        customer_id: customerId,
        customer_name: selectedCustomer.name,
        customer_address: `${selectedCustomer.street || ''} ${selectedCustomer.city || ''} ${selectedCustomer.state || ''} ${selectedCustomer.zip_code || ''}`.trim(),
        customer_contact: selectedCustomer.contact_name || selectedCustomer.name,
        customer_email: selectedCustomer.email || '',
        customer_phone: selectedCustomer.phone || ''
      });
    } else {
      setCoaFormData({
        ...coaFormData,
        customer_id: '',
        customer_name: '',
        customer_address: '',
        customer_contact: '',
        customer_email: '',
        customer_phone: ''
      });
    }
  };

  // Handle compliance standards array
  const handleComplianceStandardsChange = (e) => {
    const value = e.target.value;
    setCoaFormData({
      ...coaFormData,
      compliance_standards: typeof value === 'string' ? value.split(',') : value
    });
  };

  // Handle signature dialog open
  const handleSignatureDialogOpen = () => {
    setSignatureDialogOpen(true);
  };

  // Save signature
  const handleSaveSignature = () => {
    const signatureName = document.getElementById('signature-name')?.value || '';
    
    if (!signatureName.trim()) {
      showSnackbar('Please enter a name', 'error');
      return;
    }
    
    const signaturePosition = document.getElementById('signature-position')?.value || 'Quality Control Manager';
    
    const signatureData = {
      name: signatureName,
      position: signaturePosition,
      date: new Date().toISOString(),
      typed: true
    };
    
    setCoaFormData({
      ...coaFormData,
      signature_data: signatureData
    });
    
    setSignatureDialogOpen(false);
  };

  // Handle customer selection from dialog
  const handleCustomerSelection = (customer) => {
    setCoaFormData(prev => ({
      ...prev,
      customer_id: customer.id,
      customer_name: customer.name,
      customer_address: `${customer.street || ''} ${customer.city || ''} ${customer.state || ''} ${customer.zip_code || ''}`.trim(),
      customer_contact: customer.contact_name || customer.name,
      customer_email: customer.email || '',
      customer_phone: customer.phone || ''
    }));
    setCustomerSelectionDialogOpen(false);
    setCustomerSearchQuery('');
  };

  // Open brand page in new window
  const openBrandPage = () => {
    const companyId = formulation?.company_id;
    const url = `/company/${companyId}/brands`;
    window.open(url, '_blank');
  };

  // Open customer page in new window  
  const openCustomerPage = () => {
    const companyId = formulation?.company_id;
    const url = `/company/${companyId}/customer-new`;
    window.open(url, '_blank');
  };

  // Handle view dialog open
  const handleViewDialogOpen = (test) => {
    setViewTest(test);
    setViewDialogOpen(true);
  };

  // Generate and print Certificate of Analysis
  const handlePrintCoA = async (test) => {
    try {
      setLoading(true);
      
      const printCSS = `
        @page { 
          size: auto; 
          margin: 20mm 15mm 20mm 15mm; 
        }
        @media print {
          body { 
            padding: 0 !important; 
            -webkit-print-color-adjust: exact;
            font-size: 10pt;
          }
          .no-print { 
            display: none !important; 
          }
          .page-break {
            page-break-before: always;
          }
          .avoid-break {
            page-break-inside: avoid;
          }
          @page {
            margin: 20mm 15mm 20mm 15mm;
            @top-left { content: ""; }
            @top-center { content: ""; }
            @top-right { content: ""; }
            @bottom-left { content: ""; }
            @bottom-center { content: ""; }
            @bottom-right { content: ""; }
          }
        }
        body {
          font-family: Arial, sans-serif;
          line-height: 1.2;
          color: #000;
        }
        .header {
          text-align: center;
          font-size: 18pt;
          font-weight: bold;
          margin-bottom: 15px;
        }
        .divider {
                      border-top: 1px solid #000;
          margin: 10px 0;
        }
        .section-divider {
          border-top: 1px solid #ccc;
          margin: 8px 0;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin: 8px 0;
          font-size: 9pt;
        }
        table, th, td {
          border: 1px solid #000;
        }
        th {
          background-color: #f0f0f0;
          font-weight: bold;
        }
        th, td {
          padding: 6px 8px;
          text-align: left;
        }
        .info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin: 10px 0;
        }
        .info-section {
          margin: 8px 0;
        }
        .info-item {
          margin: 3px 0;
          font-size: 10pt;
        }
        .info-label {
          font-weight: bold;
          display: inline-block;
          min-width: 120px;
        }
        .info-value {
          display: inline-block;
          word-break: break-word;
        }
        .section-title {
          font-weight: bold;
          font-size: 12pt;
          margin: 12px 0 6px 0;
          color: #333;
        }
        .signature-section {
          margin-top: 20px;
          border-top: 1px solid #ccc;
          padding-top: 10px;
        }
        .signature-text {
          font-family: "Brush Script MT", cursive;
          font-size: 16pt;
          margin: 5px 0;
        }
        .compliance-badge {
          background: #28a745;
          color: white;
          padding: 2px 6px;
          border-radius: 3px;
          font-size: 8pt;
          font-weight: bold;
        }
        .test-result {
          font-weight: bold;
        }
        .pass { color: #28a745; }
        .fail { color: #dc3545; }
        .pending { color: #ffc107; }
      `;

      const printHTML = `
        <div class="header">Certificate of Analysis</div>
        <div class="divider"></div>
        
        <!-- Product Information -->
        <div class="info-grid">
          <div class="info-section">
            <div class="info-item">
              <span class="info-label">Product Name:</span>
              <span class="info-value">${test.product_name || ''}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Brand:</span>
              <span class="info-value">${test.brand_name || ''}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Batch Number:</span>
              <span class="info-value">${test.batch_number || ''}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Lot Size:</span>
              <span class="info-value">${test.lot_size || ''}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Manufacturing Date:</span>
              <span class="info-value">${test.manufacturing_date ? new Date(test.manufacturing_date).toLocaleDateString() : ''}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Expiry Date:</span>
              <span class="info-value">${test.expiry_date ? new Date(test.expiry_date).toLocaleDateString() : ''}</span>
            </div>
          </div>
          <div class="info-section">
            <div class="info-item">
              <span class="info-label">Reference Number:</span>
              <span class="info-value">${test.reference_number || ''}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Customer:</span>
              <span class="info-value">${test.customer_name || ''}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Customer Address:</span>
              <span class="info-value">${test.customer_address || ''}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Test Date:</span>
              <span class="info-value">${new Date(test.issued_date).toLocaleDateString()}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Test Method:</span>
              <span class="info-value">${test.test_method || 'In-house methods'}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Test Conditions:</span>
              <span class="info-value">${test.test_temperature || '25°C'}, ${test.test_humidity || '45% RH'}</span>
            </div>
          </div>
        </div>

        <!-- Physical Testing -->
        <div class="section-title avoid-break">Physical Testing</div>
        <div class="section-divider"></div>
        <table class="avoid-break">
          <tr>
            <th width="30%">Parameter</th>
            <th width="35%">Result</th>
            <th width="35%">Test Method</th>
          </tr>
          ${test.appearance ? `
          <tr>
            <td>Appearance</td>
            <td>${test.appearance}</td>
            <td>Visual Inspection</td>
          </tr>` : ''}
          ${test.color ? `
          <tr>
            <td>Color</td>
            <td>${test.color}</td>
            <td>Visual Inspection</td>
          </tr>` : ''}
          ${test.odor ? `
          <tr>
            <td>Odor</td>
            <td>${test.odor}</td>
            <td>Olfactory Assessment</td>
          </tr>` : ''}
          ${test.ph ? `
          <tr>
            <td>pH @ ${test.test_temperature || '25°C'}</td>
            <td>${test.ph}</td>
            <td>pH Meter</td>
          </tr>` : ''}
          ${test.specific_gravity ? `
          <tr>
            <td>Specific Gravity @ ${test.test_temperature || '25°C'}</td>
            <td>${test.specific_gravity}</td>
            <td>Hydrometer/Pycnometer</td>
          </tr>` : ''}
          ${test.viscosity ? `
          <tr>
            <td>Viscosity @ ${test.test_temperature || '25°C'}</td>
            <td>${test.viscosity}</td>
            <td>Brookfield Viscometer</td>
          </tr>` : ''}
          ${test.density ? `
          <tr>
            <td>Density</td>
            <td>${test.density}</td>
            <td>Density Meter</td>
          </tr>` : ''}
        </table>

        <!-- Chemical Testing -->
        ${(test.active_content || test.water_content || test.heavy_metals) ? `
        <div class="section-title avoid-break">Chemical Testing</div>
        <div class="section-divider"></div>
        <table class="avoid-break">
          <tr>
            <th width="30%">Parameter</th>
            <th width="35%">Result</th>
            <th width="35%">Test Method</th>
          </tr>
          ${test.active_content ? `
          <tr>
            <td>Active Content</td>
            <td>${test.active_content}</td>
            <td>${test.active_content_method || 'HPLC/GC'}</td>
          </tr>` : ''}
          ${test.water_content ? `
          <tr>
            <td>Water Content</td>
            <td>${test.water_content}</td>
            <td>${test.water_content_method || 'Karl Fischer'}</td>
          </tr>` : ''}
          ${test.heavy_metals ? `
          <tr>
            <td>Heavy Metals</td>
            <td><span class="test-result pass">${test.heavy_metals}</span></td>
            <td>${test.heavy_metals_method || 'ICP-MS'}</td>
          </tr>` : ''}
          ${test.preservative_content ? `
          <tr>
            <td>Preservative Content</td>
            <td>${test.preservative_content}</td>
            <td>${test.preservative_content_method || 'HPLC'}</td>
          </tr>` : ''}
        </table>` : ''}

        <!-- Microbiological Testing -->
        ${(test.total_aerobic_count || test.escherichia_coli || test.staphylococcus_aureus) ? `
        <div class="section-title avoid-break">Microbiological Testing</div>
        <div class="section-divider"></div>
        <table class="avoid-break">
          <tr>
            <th width="30%">Parameter</th>
            <th width="35%">Result</th>
            <th width="35%">Limit</th>
          </tr>
          ${test.total_aerobic_count ? `
          <tr>
            <td>Total Aerobic Count</td>
            <td>${test.total_aerobic_count}</td>
            <td>${test.total_aerobic_count_limit || '≤1000 CFU/g'}</td>
          </tr>` : ''}
          ${test.yeast_mold_count ? `
          <tr>
            <td>Yeast & Mold</td>
            <td>${test.yeast_mold_count}</td>
            <td>${test.yeast_mold_count_limit || '≤100 CFU/g'}</td>
          </tr>` : ''}
          ${test.escherichia_coli ? `
          <tr>
            <td>E. coli</td>
            <td><span class="test-result pass">${test.escherichia_coli}</span></td>
            <td>${test.escherichia_coli_limit || 'Not Detected'}</td>
          </tr>` : ''}
          ${test.staphylococcus_aureus ? `
          <tr>
            <td>S. aureus</td>
            <td><span class="test-result pass">${test.staphylococcus_aureus}</span></td>
            <td>${test.staphylococcus_aureus_limit || 'Not Detected'}</td>
          </tr>` : ''}
          ${test.pseudomonas_aeruginosa ? `
          <tr>
            <td>P. aeruginosa</td>
            <td><span class="test-result pass">${test.pseudomonas_aeruginosa}</span></td>
            <td>${test.pseudomonas_aeruginosa_limit || 'Not Detected'}</td>
          </tr>` : ''}
          ${test.candida_albicans ? `
          <tr>
            <td>Candida albicans</td>
            <td><span class="test-result pass">${test.candida_albicans}</span></td>
            <td>${test.candida_albicans_limit || 'Not Detected'}</td>
          </tr>` : ''}
        </table>` : ''}

        <!-- Compliance Information -->
        ${(test.compliance_standards && test.compliance_standards.length > 0) ? `
        <div class="section-title">Regulatory Compliance</div>
        <div class="section-divider"></div>
        <div class="info-item">
          <span class="info-label">Standards:</span>
          <span class="info-value">${test.compliance_standards.join(', ')}</span>
        </div>` : ''}
        
        ${test.gmo_status !== 'non-gmo' ? '' : `
        <div class="info-item">
          <span class="compliance-badge">NON-GMO</span>
        </div>`}
        
        ${!test.cruelty_free ? '' : `
        <div class="info-item">
          <span class="compliance-badge">CRUELTY FREE</span>
        </div>`}

        <!-- Additional Information -->
        ${test.ingredient_disclosure ? `
        <div class="section-title">Ingredient Disclosure</div>
        <div class="section-divider"></div>
        <div style="margin: 8px 0; font-size: 10pt;">
          ${test.ingredient_disclosure}
        </div>` : ''}

        ${test.safety_warnings ? `
        <div class="section-title">Safety Information</div>
        <div class="section-divider"></div>
        <div style="margin: 8px 0; font-size: 10pt;">
          ${test.safety_warnings}
        </div>` : ''}

        <!-- Signature Section -->
        <div class="signature-section">
          <div class="info-grid">
            <div class="info-section">
              <div class="info-item">
                <span class="info-label">QC Manager:</span>
                <span class="info-value">${test.qc_manager || ''}</span>
              </div>
              <div class="info-item">
                <span class="info-label">QC Technician:</span>
                <span class="info-value">${test.qc_technician || ''}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Certificate Valid Until:</span>
                <span class="info-value">${test.certificate_validity_period || '2 years'} from issue date</span>
              </div>
            </div>
            <div class="info-section">
              <div class="info-item">
                <span class="info-label">Date Issued:</span>
                <span class="info-value">${new Date(test.issued_date).toLocaleDateString()}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Authorized Signature:</span>
                ${test.signature_data && test.signature_data.name ? 
                  `<div class="signature-text">${test.signature_data.name}</div>
                   <div style="font-size: 9pt;">${test.signature_data.position || 'Quality Control Manager'}</div>` : 
                  '<div style="height: 30px; border-bottom: 1px solid #000; width: 200px; margin: 10px 0;"></div>'}
              </div>
            </div>
          </div>
        </div>
      `;
      
      const iframeId = 'coa-print-frame';
      let iframe = document.getElementById(iframeId);
      
      if (iframe) {
        document.body.removeChild(iframe);
      }
      
      iframe = document.createElement('iframe');
      iframe.id = iframeId;
      iframe.style.position = 'absolute';
      iframe.style.top = '-9999px';
      iframe.style.left = '-9999px';
      iframe.style.width = '0';
      iframe.style.height = '0';
      document.body.appendChild(iframe);
      
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      
      iframeDoc.open();
      iframeDoc.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Certificate of Analysis</title>
          <style>${printCSS}</style>
        </head>
        <body>
          ${printHTML}
        </body>
        </html>
      `);
      iframeDoc.close();
      
      iframe.onload = () => {
        try {
          iframe.contentWindow.focus();
          
          // Try to disable browser headers/footers
          try {
            const printSettings = {
              shouldPrintBackgrounds: true,
              shouldPrintSelectionOnly: false,
              mediaType: 'print',
              headerFooterEnabled: false
            };
            
            if (iframe.contentWindow.chrome && iframe.contentWindow.chrome.webstore) {
              iframe.contentWindow.print();
            } else {
              iframe.contentWindow.print();
            }
          } catch (e) {
            iframe.contentWindow.print();
          }
          
          setTimeout(() => {
            if (document.body.contains(iframe)) {
              document.body.removeChild(iframe);
            }
            setLoading(false);
          }, 1000);
        } catch (error) {
          console.error('Error printing:', error);
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
          setLoading(false);
          showSnackbar('Error printing Certificate of Analysis', 'error');
        }
      };
      
    } catch (error) {
      console.error('Error generating Certificate of Analysis:', error);
      showSnackbar('Error generating Certificate of Analysis', 'error');
      setLoading(false);
    }
  };

  // Save Certificate of Analysis
  const handleSaveCoA = async () => {
    try {
      setLoading(true);
      
      if (!formulation?.id) {
        showSnackbar('No formulation selected', 'error');
        setLoading(false);
        return;
      }
      
      if (!coaFormData.batch_number || !coaFormData.product_name) {
        showSnackbar('Please fill in Batch Number and Product Name', 'error');
        setLoading(false);
        return;
      }
      
      // Prepare the COA data for database storage with proper date handling
      const coaData = {
        formulation_id: formulation.id,
        test_type: 'certificate_of_analysis',
        test_date: coaFormData.issued_date,
        status: coaFormData.coa_status,
        results: 'Certificate of Analysis',
        // Basic Info
        batch_number: coaFormData.batch_number,
        brand_name: coaFormData.brand_name,
        product_name: coaFormData.product_name,
        reference_number: coaFormData.reference_number,
        // Customer Info
        customer_id: coaFormData.customer_id || null,
        customer_name: coaFormData.customer_name,
        customer_address: coaFormData.customer_address,
        customer_contact: coaFormData.customer_contact,
        customer_email: coaFormData.customer_email,
        customer_phone: coaFormData.customer_phone,
        // Physical Testing
        appearance: coaFormData.appearance,
        color: coaFormData.color,
        odor: coaFormData.odor,
        ph: coaFormData.ph,
        specific_gravity: coaFormData.specific_gravity,
        viscosity: coaFormData.viscosity,
        density: coaFormData.density,
        melting_point: coaFormData.melting_point,
        boiling_point: coaFormData.boiling_point,
        refractive_index: coaFormData.refractive_index,
        // Chemical Testing
        active_content: coaFormData.active_content,
        active_content_method: coaFormData.active_content_method,
        water_content: coaFormData.water_content,
        water_content_method: coaFormData.water_content_method,
        ash_content: coaFormData.ash_content,
        ash_content_method: coaFormData.ash_content_method,
        heavy_metals: coaFormData.heavy_metals,
        heavy_metals_method: coaFormData.heavy_metals_method,
        preservative_content: coaFormData.preservative_content,
        preservative_content_method: coaFormData.preservative_content_method,
        // Microbiological Testing
        total_aerobic_count: coaFormData.total_aerobic_count,
        total_aerobic_count_limit: coaFormData.total_aerobic_count_limit,
        yeast_mold_count: coaFormData.yeast_mold_count,
        yeast_mold_count_limit: coaFormData.yeast_mold_count_limit,
        escherichia_coli: coaFormData.escherichia_coli,
        escherichia_coli_limit: coaFormData.escherichia_coli_limit,
        staphylococcus_aureus: coaFormData.staphylococcus_aureus,
        staphylococcus_aureus_limit: coaFormData.staphylococcus_aureus_limit,
        pseudomonas_aeruginosa: coaFormData.pseudomonas_aeruginosa,
        pseudomonas_aeruginosa_limit: coaFormData.pseudomonas_aeruginosa_limit,
        candida_albicans: coaFormData.candida_albicans,
        candida_albicans_limit: coaFormData.candida_albicans_limit,
        // Manufacturing Info with proper date handling
        manufacturing_date: coaFormData.manufacturing_date || null,
        expiry_date: coaFormData.expiry_date || null,
        manufacturing_location: coaFormData.manufacturing_location,
        lot_size: coaFormData.lot_size,
        packaging_type: coaFormData.packaging_type,
        storage_conditions: coaFormData.storage_conditions,
        // Quality Control
        qc_manager: coaFormData.qc_manager,
        qc_technician: coaFormData.qc_technician,
        test_method: coaFormData.test_method,
        equipment_used: coaFormData.equipment_used,
        test_temperature: coaFormData.test_temperature,
        test_humidity: coaFormData.test_humidity,
        // Regulatory & Compliance
        regulatory_status: coaFormData.regulatory_status,
        compliance_standards: coaFormData.compliance_standards,
        allergen_info: coaFormData.allergen_info,
        gmo_status: coaFormData.gmo_status,
        halal_status: coaFormData.halal_status,
        kosher_status: coaFormData.kosher_status,
        organic_status: coaFormData.organic_status,
        cruelty_free: coaFormData.cruelty_free,
        vegan_status: coaFormData.vegan_status,
        // Additional Info
        ingredient_disclosure: coaFormData.ingredient_disclosure,
        safety_warnings: coaFormData.safety_warnings,
        shelf_life: coaFormData.shelf_life,
        country_of_origin: coaFormData.country_of_origin,
        certificate_validity_period: coaFormData.certificate_validity_period,
        retesting_date: coaFormData.retesting_date || null,
        additional_notes: coaFormData.additional_notes,
        // Meta
        issued_date: coaFormData.issued_date || null,
        coa_status: coaFormData.coa_status,
        signature_data: coaFormData.signature_data,
        updated_at: new Date()
      };
      
      if (currentTest && currentTest.test_type === 'certificate_of_analysis') {
        const { data, error } = await supabase
          .from('formulation_tests')
          .update(coaData)
          .eq('id', currentTest.id)
          .select();
          
        if (error) throw error;
        
        if (data && data[0]) {
          setTests(prevTests => 
            prevTests.map(test => test.id === data[0].id ? data[0] : test)
          );
          showSnackbar('Certificate of Analysis updated successfully', 'success');
        } else {
          await fetchTests();
          showSnackbar('Certificate of Analysis updated successfully', 'success');
        }
      } else {
        const { data, error } = await supabase
          .from('formulation_tests')
          .insert({
            ...coaData,
            created_at: new Date(),
            created_by: user?.id
          })
          .select();
          
        if (error) throw error;
        
        if (data && data.length > 0) {
          setTests(prevTests => [...prevTests, data[0]]);
          showSnackbar('Certificate of Analysis created successfully', 'success');
        } else {
          await fetchTests();
          showSnackbar('Certificate of Analysis created successfully', 'success');
        }
      }
      
      setCoaDialogOpen(false);
    } catch (error) {
      console.error('Error saving Certificate of Analysis:', error);
      showSnackbar('Error saving Certificate of Analysis: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle CoA deletion
  const handleDeleteCoA = (test) => {
    setTestToDelete(test);
    setDeleteConfirmStep(1);
    setDeleteConfirmText('');
    setDeleteDialogOpen(true);
  };

  // Handle delete confirmation dialog actions
  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('formulation_tests')
        .delete()
        .eq('id', testToDelete.id);
        
      if (error) throw error;
      
      setTests(tests.filter(t => t.id !== testToDelete.id));
      
      setDeleteDialogOpen(false);
      setTestToDelete(null);
      
      showSnackbar('Certificate of Analysis deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting record:', error);
      showSnackbar('Error deleting record: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle cancel delete
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDeleteConfirmStep(1);
    setTestToDelete(null);
    setDeleteConfirmText('');
  };

  return (
    <>
      {/* Brand Selection Dialog */}
      <Dialog
        open={brandSelectionDialogOpen}
        onClose={() => {
          setBrandSelectionDialogOpen(false);
          setBrandSearchQuery('');
        }}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 4,
            border: 'none',
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 4, 
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
          color: theme.palette.text.primary,
          fontWeight: 500
        }}>
          Select Brand
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Box sx={{ mb: 4, mt: 3 }}>
            <TextField
              fullWidth
              placeholder="Search brands..."
              value={brandSearchQuery}
              onChange={(e) => setBrandSearchQuery(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  '& fieldset': {
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    borderWidth: 1
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main
                  }
                }
              }}
            />
          </Box>
          
          <Box sx={{ 
            maxHeight: 400, 
            overflowY: 'auto',
            border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            borderRadius: 2,
            background: 'white',
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            scrollbarWidth: 'none'
          }}>
            {brands
              .filter(brand => 
                brand.name.toLowerCase().includes(brandSearchQuery.toLowerCase())
              )
              .map(brand => (
                <Box
                  key={brand.id}
                  onClick={() => {
                    setCoaFormData(prev => ({
                      ...prev,
                      brand_name: brand.name
                    }));
                    setBrandSelectionDialogOpen(false);
                    setBrandSearchQuery('');
                  }}
                  sx={{
                    p: 3,
                    borderBottom: `1px solid ${alpha(theme.palette.grey[200], 0.8)}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                      transform: 'translateX(4px)',
                      boxShadow: 'inset 4px 0 0 black'
                    },
                    '&:last-child': {
                      borderBottom: 'none'
                    }
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    {brand.name}
                  </Typography>
                </Box>
              ))
            }
            {brands
              .filter(brand => 
                brand.name.toLowerCase().includes(brandSearchQuery.toLowerCase())
              ).length === 0 && (
              <Box sx={{ p: 6, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No brands found matching your search.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: 4, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
        }}>
          <Button 
            startIcon={<Add />}
            onClick={openBrandPage}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              color: theme.palette.primary.main,
              fontWeight: 500,
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04)
              }
            }}
          >
            Add Brand
          </Button>
          <Button 
            onClick={() => {
              setBrandSelectionDialogOpen(false);
              setBrandSearchQuery('');
            }}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04)
              }
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>



      {/* Customer Selection Dialog */}
      <Dialog
        open={customerSelectionDialogOpen}
        onClose={() => {
          setCustomerSelectionDialogOpen(false);
          setCustomerSearchQuery('');
        }}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 4,
            border: 'none',
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 4, 
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
          color: theme.palette.text.primary,
          fontWeight: 500
        }}>
          Select Customer
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Box sx={{ mb: 4, mt: 3 }}>
            <TextField
              fullWidth
              placeholder="Search customers..."
              value={customerSearchQuery}
              onChange={(e) => setCustomerSearchQuery(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  '& fieldset': {
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    borderWidth: 1
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main
                  }
                }
              }}
            />
          </Box>
          
          <Box sx={{ 
            maxHeight: 400, 
            overflowY: 'auto',
            border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            borderRadius: 2,
            background: 'white',
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            scrollbarWidth: 'none'
          }}>
            {customers
              .filter(customer => 
                customer.name.toLowerCase().includes(customerSearchQuery.toLowerCase())
              )
              .map(customer => (
                <Box
                  key={customer.id}
                  onClick={() => handleCustomerSelection(customer)}
                  sx={{
                    p: 3,
                    borderBottom: `1px solid ${alpha(theme.palette.grey[200], 0.8)}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                      transform: 'translateX(4px)',
                      boxShadow: 'inset 4px 0 0 black'
                    },
                    '&:last-child': {
                      borderBottom: 'none'
                    }
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 0.5 }}>
                    {customer.name}
                  </Typography>
                  {customer.email && (
                    <Typography variant="body2" color="text.secondary">
                      {customer.email}
                    </Typography>
                  )}
                </Box>
              ))
            }
            {customers
              .filter(customer => 
                customer.name.toLowerCase().includes(customerSearchQuery.toLowerCase())
              ).length === 0 && (
              <Box sx={{ p: 6, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No customers found matching your search.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: 4, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
        }}>
          <Button 
            startIcon={<Add />}
            onClick={openCustomerPage}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              color: theme.palette.primary.main,
              fontWeight: 500,
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04)
              }
            }}
          >
            Add Customer
          </Button>
          <Button 
            onClick={() => {
              setCustomerSelectionDialogOpen(false);
              setCustomerSearchQuery('');
            }}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04)
              }
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Certificates of Analysis Section */}
      <Accordion 
        expanded={coaExpanded}
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 3,
                      border: `1px solid ${theme.palette.grey[400]}`,
          background: `linear-gradient(${alpha(theme.palette.success.main, 0.08)}, ${alpha(theme.palette.success.main, 0.02)})`,
          backdropFilter: 'blur(20px)',
          overflow: 'hidden',
          boxShadow: `0 4px 20px ${alpha(theme.palette.success.main, 0.08)}`,
          '&:before': {
            display: 'none'
          },
          '&.Mui-expanded': {
            margin: '0 0 24px 0'
          },
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.success.main, 0.12)}`
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
        onChange={handleCoaExpandChange}
      >
        <AccordionSummary 
          expandIcon={<ExpandMore sx={{ color: theme.palette.success.main }} />}
          sx={{
            borderRadius: '12px 12px 0 0',
            background: 'white',
            backdropFilter: 'blur(10px)',
            border: 'none',
            minHeight: 72,
            '&.Mui-expanded': {
              minHeight: 72,
              borderRadius: '12px 12px 0 0'
            },
            '& .MuiAccordionSummary-content': {
              margin: '16px 0',
              alignItems: 'center',
              '&.Mui-expanded': {
                margin: '16px 0'
              }
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)}, ${alpha(theme.palette.success.main, 0.05)})`,
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
              color: theme.palette.success.main,
              mr: 3
            }}>
              <AssignmentIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                Certificates of Analysis
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Official quality control certificates for product batches
              </Typography>
            </Box>
            <Chip 
              label={`${tests.filter(test => test.test_type === 'certificate_of_analysis').length} ${tests.filter(test => test.test_type === 'certificate_of_analysis').length === 1 ? 'certificate' : 'certificates'}`}
              size="small"
              variant="outlined"
              sx={{ 
                fontWeight: 500,
                borderColor: theme.palette.success.main,
                color: theme.palette.success.main,
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                mr: 2
              }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {/* Action Button Section */}
          <Box sx={{ 
            p: 2, 
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            justifyContent: 'flex-end'
          }}>
            {canEdit && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleCoADialogOpen()}
                sx={{ 
                  borderRadius: 3,
                  px: 3,
                  py: 1.5,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  fontWeight: 500,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                  transform: 'translateY(-1px)',
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                },
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              Create Certificate of Analysis
            </Button>
            )}
          </Box>

          {/* Content Section */}
          <Box sx={{ p: 4 }}>
            {tests.filter(test => test.test_type === 'certificate_of_analysis').length > 0 ? (
              <TableContainer 
                sx={{ 
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
                  boxShadow: `0 12px 32px ${alpha(theme.palette.success.main, 0.15)}`,
                  overflow: 'hidden',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 20px 40px ${alpha(theme.palette.success.main, 0.25)}`,
                    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`
                  }
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `1px solid ${theme.palette.success.main}`,
                          color: 'success.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Product Name
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `1px solid ${theme.palette.success.main}`,
                          color: 'success.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Batch Number
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `1px solid ${theme.palette.success.main}`,
                          color: 'success.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Brand
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `1px solid ${theme.palette.success.main}`,
                          color: 'success.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Customer
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `1px solid ${theme.palette.success.main}`,
                          color: 'success.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Reference #
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `1px solid ${theme.palette.success.main}`,
                          color: 'success.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Issue Date
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `1px solid ${theme.palette.success.main}`,
                          color: 'success.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Status
                      </TableCell>
                      <TableCell 
                        align="right" 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `1px solid ${theme.palette.success.main}`,
                          color: 'success.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tests
                      .filter(test => test.test_type === 'certificate_of_analysis')
                      .sort((a, b) => new Date(b.created_at || b.test_date) - new Date(a.created_at || a.test_date))
                      .map((test) => (
                        <TableRow 
                          key={test.id} 
                          hover 
                          sx={{ 
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': { 
                              bgcolor: 'grey.50',
                              transform: 'translateX(4px)',
                              transition: 'all 0.2s ease',
                              boxShadow: 'inset 4px 0 0 black'
                            },
                            '& .MuiTableCell-root': {
                              borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                              py: 2.5,
                              px: 3
                            }
                          }}
                          onClick={() => handleViewDialogOpen(test)}
                        >
                          <TableCell>{test.product_name || 'Untitled Product'}</TableCell>
                          <TableCell>{test.batch_number || '—'}</TableCell>
                          <TableCell>{test.brand_name || '—'}</TableCell>
                          <TableCell>{test.customer_name || '—'}</TableCell>
                          <TableCell>{test.reference_number || '—'}</TableCell>
                          <TableCell>{new Date(test.issued_date).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <Chip 
                              size="small" 
                              label={test.coa_status || 'draft'}
                              color={
                                test.coa_status === 'approved' ? 'success' : 
                                test.coa_status === 'issued' ? 'primary' :
                                test.coa_status === 'rejected' ? 'error' : 
                                'default'
                              }
                              sx={{ 
                                fontWeight: 500,
                                textTransform: 'capitalize'
                              }}
                            />
                          </TableCell>
                          <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                            <Tooltip title="View">
                              <IconButton size="small" onClick={() => handleViewDialogOpen(test)}>
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {canEdit && (
                              <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => handleCoADialogOpen(test)}>
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Print">
                              <IconButton 
                                size="small" 
                                color="primary" 
                                onClick={() => handlePrintCoA(test)}
                              >
                                <Print fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {canDelete && (
                              <Tooltip title="Delete">
                                <IconButton size="small" color="error" onClick={() => handleDeleteCoA(test)}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Paper 
                elevation={0}
                sx={{ 
                  p: 6, 
                  textAlign: 'center',
                  borderRadius: 3,
                  border: `2px dashed ${alpha(theme.palette.success.main, 0.2)}`,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.03)}, ${alpha(theme.palette.success.main, 0.01)})`
                }}
              >
                <Box sx={{ 
                  p: 3,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)}, ${alpha(theme.palette.success.main, 0.04)})`,
                  display: 'inline-block',
                  mb: 3
                }}>
                  <AssignmentIcon sx={{ fontSize: 64, color: 'success.main' }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 500, mb: 2, color: 'text.primary' }}>
                  No Certificates Created Yet
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto', fontWeight: 500 }}>
                  Create official certificates of analysis for quality control and regulatory compliance.
                </Typography>
                {canEdit && (
                  <Button 
                    variant="contained" 
                    startIcon={<Add />} 
                    onClick={() => handleCoADialogOpen()}
                    sx={{ 
                      borderRadius: 3,
                      px: 4,
                      py: 1.5,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                      fontWeight: 500,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                      '&:hover': {
                        background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    Create First Certificate
                  </Button>
                )}
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Certificate of Analysis Dialog */}
      <Dialog 
        open={coaDialogOpen} 
        onClose={() => setCoaDialogOpen(false)} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: 'none',
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 4, 
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
          color: theme.palette.text.primary,
          fontWeight: 500
        }}>
          {currentTest && currentTest.test_type === 'certificate_of_analysis' 
            ? 'Edit Certificate of Analysis' 
            : 'Create Certificate of Analysis'
          }
        </DialogTitle>
        <DialogContent sx={{ p: 4, maxHeight: '80vh', overflowY: 'auto'}}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid container spacing={3}>
            {/* Product Information Section */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: theme.palette.primary.main, mb: 2, mt: 2}}>
                Product Information
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="batch_number"
                label="Batch Number"
                value={coaFormData.batch_number}
                onChange={handleCoAChange}
                fullWidth
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)'
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Button 
                fullWidth 
                variant="outlined" 
                onClick={() => setBrandSelectionDialogOpen(true)}
                sx={{ 
                  borderRadius: 2,
                  py: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  color: coaFormData.brand_name ? theme.palette.text.primary : theme.palette.text.secondary,
                  fontWeight: 500,
                  justifyContent: 'flex-start',
                  textAlign: 'left'
                }}
              >
                {coaFormData.brand_name || 'Select Brand'}
              </Button>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="product_name"
                label="Product Name"
                value={coaFormData.product_name}
                fullWidth
                InputProps={{ readOnly: true }}
                helperText="Auto-populated from formulation"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'white'
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="reference_number"
                label="Reference Number"
                value={coaFormData.reference_number}
                onChange={handleCoAChange}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <Tooltip title="Generate Reference Number">
                      <IconButton 
                        edge="end" 
                        onClick={() => {
                          setCoaFormData({
                            ...coaFormData,
                            reference_number: generateReferenceNumber()
                          });
                        }}
                      >
                        <Refresh fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="lot_size"
                label="Lot Size"
                value={coaFormData.lot_size}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>COA Status</InputLabel>
                <Select
                  name="coa_status"
                  value={coaFormData.coa_status}
                  onChange={handleCoAChange}
                  label="COA Status"
                  sx={{
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }}
                >
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="pending">Pending Review</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="issued">Issued</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Customer Information Section */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: theme.palette.primary.main, mb: 2, mt: 4 }}>
                Customer Information
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Button 
                fullWidth 
                variant="outlined" 
                onClick={() => setCustomerSelectionDialogOpen(true)}
                sx={{ 
                  borderRadius: 2,
                  py: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  color: coaFormData.customer_name ? theme.palette.text.primary : theme.palette.text.secondary,
                  fontWeight: 500,
                  justifyContent: 'flex-start',
                  textAlign: 'left'
                }}
              >
                {coaFormData.customer_name || 'Select Customer'}
              </Button>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="customer_contact"
                label="Customer Contact"
                value={coaFormData.customer_contact}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="customer_email"
                label="Customer Email"
                value={coaFormData.customer_email}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="customer_phone"
                label="Customer Phone"
                value={coaFormData.customer_phone}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                name="customer_address"
                label="Customer Address"
                value={coaFormData.customer_address}
                onChange={handleCoAChange}
                fullWidth
                multiline
                rows={3}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            {/* Manufacturing Information Section */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: theme.palette.primary.main, mb: 2, mt: 4 }}>
                Manufacturing Information
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Manufacturing Date"
                value={coaFormData.manufacturing_date ? dayjs(coaFormData.manufacturing_date) : null}
                onChange={(newValue) => {
                  setCoaFormData({
                    ...coaFormData,
                    manufacturing_date: newValue ? newValue.format('YYYY-MM-DD') : ''
                  });
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)'
                      }
                    }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Expiry Date"
                value={coaFormData.expiry_date ? dayjs(coaFormData.expiry_date) : null}
                onChange={(newValue) => {
                  setCoaFormData({
                    ...coaFormData,
                    expiry_date: newValue ? newValue.format('YYYY-MM-DD') : ''
                  });
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)'
                      }
                    }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="manufacturing_location"
                label="Manufacturing Location"
                value={coaFormData.manufacturing_location}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="packaging_type"
                label="Packaging Type"
                value={coaFormData.packaging_type}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="storage_conditions"
                label="Storage Conditions"
                value={coaFormData.storage_conditions}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>
            
            {/* Physical Testing Section */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: theme.palette.primary.main, mb: 2, mt: 4 }}>
                Physical Testing
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                name="appearance"
                label="Appearance"
                value={coaFormData.appearance}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                name="color"
                label="Color"
                value={coaFormData.color}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                name="odor"
                label="Odor"
                value={coaFormData.odor}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                name="ph"
                label="pH @ 25°C"
                value={coaFormData.ph}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                name="specific_gravity"
                label="Specific Gravity @ 25°C"
                value={coaFormData.specific_gravity}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                name="viscosity"
                label="Viscosity @ 25°C"
                value={coaFormData.viscosity}
                onChange={handleCoAChange}
                fullWidth
                helperText="e.g., 27813 cps"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                name="density"
                label="Density"
                value={coaFormData.density}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                name="melting_point"
                label="Melting Point"
                value={coaFormData.melting_point}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                name="refractive_index"
                label="Refractive Index"
                value={coaFormData.refractive_index}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            {/* Chemical Testing Section */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: theme.palette.primary.main, mb: 2, mt: 4 }}>
                Chemical Testing
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="active_content"
                label="Active Content"
                value={coaFormData.active_content}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="active_content_method"
                label="Active Content Test Method"
                value={coaFormData.active_content_method}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="water_content"
                label="Water Content"
                value={coaFormData.water_content}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="water_content_method"
                label="Water Content Test Method"
                value={coaFormData.water_content_method}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="ash_content"
                label="Ash Content"
                value={coaFormData.ash_content}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="heavy_metals"
                label="Heavy Metals"
                value={coaFormData.heavy_metals}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="heavy_metals_method"
                label="Heavy Metals Test Method"
                value={coaFormData.heavy_metals_method}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="preservative_content"
                label="Preservative Content"
                value={coaFormData.preservative_content}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="preservative_content_method"
                label="Preservative Content Test Method"
                value={coaFormData.preservative_content_method}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            {/* Microbiological Testing Section */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: theme.palette.primary.main, mb: 2, mt: 4 }}>
                Microbiological Testing
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="total_aerobic_count"
                label="Total Aerobic Count"
                value={coaFormData.total_aerobic_count}
                onChange={handleCoAChange}
                fullWidth
                helperText="Result"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="total_aerobic_count_limit"
                label="Total Aerobic Count Limit"
                value={coaFormData.total_aerobic_count_limit}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="yeast_mold_count"
                label="Yeast & Mold Count"
                value={coaFormData.yeast_mold_count}
                onChange={handleCoAChange}
                fullWidth
                helperText="Result"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="yeast_mold_count_limit"
                label="Yeast & Mold Count Limit"
                value={coaFormData.yeast_mold_count_limit}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="escherichia_coli"
                label="E. coli"
                value={coaFormData.escherichia_coli}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="escherichia_coli_limit"
                label="E. coli Limit"
                value={coaFormData.escherichia_coli_limit}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="staphylococcus_aureus"
                label="S. aureus"
                value={coaFormData.staphylococcus_aureus}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="staphylococcus_aureus_limit"
                label="S. aureus Limit"
                value={coaFormData.staphylococcus_aureus_limit}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="pseudomonas_aeruginosa"
                label="P. aeruginosa"
                value={coaFormData.pseudomonas_aeruginosa}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="pseudomonas_aeruginosa_limit"
                label="P. aeruginosa Limit"
                value={coaFormData.pseudomonas_aeruginosa_limit}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="candida_albicans"
                label="Candida albicans"
                value={coaFormData.candida_albicans}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="candida_albicans_limit"
                label="Candida albicans Limit"
                value={coaFormData.candida_albicans_limit}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            {/* Quality Control Section */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: theme.palette.primary.main, mb: 2, mt: 4 }}>
                Quality Control
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="qc_manager"
                label="QC Manager"
                value={coaFormData.qc_manager}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="qc_technician"
                label="QC Technician"
                value={coaFormData.qc_technician}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="test_method"
                label="Test Method"
                value={coaFormData.test_method}
                onChange={handleCoAChange}
                fullWidth
                placeholder="e.g., In-house methods / USP / ASTM / Visual Inspection / pH Meter / HPLC"
                helperText="Specify the testing methods used for analysis"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="equipment_used"
                label="Equipment Used"
                value={coaFormData.equipment_used}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="test_temperature"
                label="Test Temperature"
                value={coaFormData.test_temperature}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="test_humidity"
                label="Test Humidity"
                value={coaFormData.test_humidity}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            {/* Regulatory & Compliance Section */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: theme.palette.primary.main, mb: 2, mt: 4 }}>
                Regulatory & Compliance
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Compliance Standards</InputLabel>
                <Select
                  multiple
                  name="compliance_standards"
                  value={coaFormData.compliance_standards}
                  onChange={handleComplianceStandardsChange}
                  label="Compliance Standards"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }}
                >
                  <MenuItem value="ISO 9001">ISO 9001</MenuItem>
                  <MenuItem value="ISO 22716">ISO 22716</MenuItem>
                  <MenuItem value="GMP">GMP</MenuItem>
                  <MenuItem value="FDA">FDA</MenuItem>
                  <MenuItem value="EU Cosmetics">EU Cosmetics</MenuItem>
                  <MenuItem value="ASTM">ASTM</MenuItem>
                  <MenuItem value="USP">USP</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>GMO Status</InputLabel>
                <Select
                  name="gmo_status"
                  value={coaFormData.gmo_status}
                  onChange={handleCoAChange}
                  label="GMO Status"
                  sx={{
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }}
                >
                  <MenuItem value="non-gmo">Non-GMO</MenuItem>
                  <MenuItem value="gmo">Contains GMO</MenuItem>
                  <MenuItem value="unknown">Unknown</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="cruelty_free"
                    checked={coaFormData.cruelty_free}
                    onChange={handleCoAChange}
                    color="primary"
                  />
                }
                label="Cruelty Free"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="vegan_status"
                    checked={coaFormData.vegan_status}
                    onChange={handleCoAChange}
                    color="primary"
                  />
                }
                label="Vegan"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="allergen_info"
                label="Allergen Information"
                value={coaFormData.allergen_info}
                onChange={handleCoAChange}
                fullWidth
                multiline
                rows={2}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            {/* Additional Information Section */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: theme.palette.primary.main, mb: 2, mt: 4 }}>
                Additional Information
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="shelf_life"
                label="Shelf Life"
                value={coaFormData.shelf_life}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="country_of_origin"
                label="Country of Origin"
                value={coaFormData.country_of_origin}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="certificate_validity_period"
                label="Certificate Validity Period"
                value={coaFormData.certificate_validity_period}
                onChange={handleCoAChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Issue Date"
                value={coaFormData.issued_date ? dayjs(coaFormData.issued_date) : null}
                onChange={(newValue) => {
                  setCoaFormData({
                    ...coaFormData,
                    issued_date: newValue ? newValue.format('YYYY-MM-DD') : ''
                  });
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)'
                      }
                    }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="ingredient_disclosure"
                label="Ingredient Disclosure"
                value={coaFormData.ingredient_disclosure}
                onChange={handleCoAChange}
                fullWidth
                multiline
                rows={4}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="safety_warnings"
                label="Safety Warnings"
                value={coaFormData.safety_warnings}
                onChange={handleCoAChange}
                fullWidth
                multiline
                rows={3}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="additional_notes"
                label="Additional Notes"
                value={coaFormData.additional_notes}
                onChange={handleCoAChange}
                fullWidth
                multiline
                rows={3}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            </Grid>
            
            {/* Signature Section */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: theme.palette.primary.main, mb: 2, mt: 4 }}>
                Signature
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {coaFormData.signature_data ? (
                  <Box sx={{ border: '1px solid #ddd', p: 2, borderRadius: 1, mr: 2, minWidth: 200 }}>
                    <Box sx={{ 
                      fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                      fontSize: '28px', 
                      color: '#000000',
                      mb: 1,
                      textShadow: '1px 1px 1px rgba(0,0,0,0.1)'
                    }}>
                      {coaFormData.signature_data.name}
                    </Box>
                    <Typography variant="caption" display="block">
                      <strong>Signed by:</strong> {coaFormData.signature_data.name}
                    </Typography>
                    {coaFormData.signature_data.position ? (
                      <Typography variant="caption" display="block">
                        <strong>Position:</strong> {coaFormData.signature_data.position}
                      </Typography>
                    ) : null}
                    <Typography variant="caption" display="block">
                      <strong>Date:</strong> {new Date(coaFormData.signature_data.date).toLocaleDateString()}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                    No signature provided
                  </Typography>
                )}
                
                <Button 
                  variant="outlined" 
                  onClick={handleSignatureDialogOpen}
                  sx={{ 
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    color: theme.palette.primary.main,
                    fontWeight: 500
                  }}
                >
                  {coaFormData.signature_data ? 'Change Signature' : 'Add Signature'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </LocalizationProvider>
        </DialogContent>
        <DialogActions sx={{ 
          p: 4, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
        }}>
          <Button 
            onClick={() => setCoaDialogOpen(false)}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04)
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveCoA} 
            variant="contained" 
            disabled={loading}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              color: 'white',
              fontWeight: 500,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
              },
              '&:disabled': {
                background: theme.palette.grey[400],
                color: theme.palette.grey[100]
              }
            }}
          >
            {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Signature Dialog */}
      <Dialog 
        open={signatureDialogOpen} 
        onClose={() => setSignatureDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: 'none',
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 4, 
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
          color: theme.palette.text.primary,
          fontWeight: 500
        }}>
          Add Signature
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, mt: 2 }}>
                Enter Signature Information:
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                id="signature-name"
                label="Name"
                fullWidth
                margin="normal"
                autoFocus
                required
                value={coaFormData.signature_data?.name || ''}
                onChange={(e) => {
                  const updatedSignature = {
                    ...coaFormData.signature_data,
                    name: e.target.value,
                    date: new Date().toISOString(),
                    typed: true
                  };
                  setCoaFormData({
                    ...coaFormData,
                    signature_data: updatedSignature
                  });
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                id="signature-position"
                label="Position/Title"
                fullWidth
                margin="normal"
                value={coaFormData.signature_data?.position || 'Quality Control Manager'}
                onChange={(e) => {
                  const updatedSignature = {
                    ...coaFormData.signature_data,
                    position: e.target.value,
                    date: new Date().toISOString(),
                    typed: true
                  };
                  setCoaFormData({
                    ...coaFormData,
                    signature_data: updatedSignature
                  });
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ mt: 2, p: 2, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
                <Typography variant="body2" gutterBottom sx={{ fontWeight: 500 }}>
                  Your signature will appear in script font on the Certificate of Analysis.
                </Typography>
                {coaFormData.signature_data?.name && (
                  <Box sx={{ 
                    fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                    fontSize: '28px', 
                    color: '#000000',
                    mt: 1,
                    mb: 1,
                    textShadow: '1px 1px 1px rgba(0,0,0,0.1)'
                  }}>
                    {coaFormData.signature_data.name}
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          p: 4, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
        }}>
          <Button 
            onClick={() => setSignatureDialogOpen(false)}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04)
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveSignature} 
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              color: 'white',
              fontWeight: 500,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
              }
            }}
          >
            Save Signature
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={handleCancelDelete} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: 'none',
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 24px 48px ${alpha(theme.palette.grey[900], 0.15)}`,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 4, 
          borderBottom: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)}, ${alpha(theme.palette.error.main, 0.04)})`,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Box sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.error.main, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <DeleteIcon sx={{ color: 'error.main', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem', color: 'text.primary' }}>
              Confirm Delete
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mt: 0.5 }}>
              This action cannot be undone
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'text.primary', mt: 2 }}>
            Are you sure you want to delete this certificate? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '1px solid', 
          borderColor: 'divider', 
          p: 2, 
          justifyContent: 'space-between' 
        }}>
          <Button 
            onClick={handleCancelDelete}
            sx={{ 
              px: 3,
              borderRadius: 3,
              py: 1.5
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            onClick={handleDeleteConfirm}
            sx={{
              px: 3,
              borderRadius: 3,
              py: 1.5,
              bgcolor: 'error.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'error.dark',
                transform: 'translateY(-1px)'
              }
            }}
          >
            Delete Certificate
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Certificate of Analysis Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false);
          setViewTest(null);
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 4,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            maxHeight: '90vh',
            boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
            bgcolor: alpha(theme.palette.grey[50], 0.95),
            backdropFilter: 'blur(10px)'
          }
        }}
      >
        {viewTest && (
          <>
            <DialogTitle sx={{ 
              pb: 2, 
              pt: 3,
              px: 3,
              fontSize: '1.25rem', 
              fontWeight: 500,
              color: 'text.primary',
              borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: 'white'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                }}>
                  <AssignmentIcon sx={{ fontSize: 24, color: 'success.main' }} />
                </Box>
                <Box>
                  <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem', color: 'text.primary' }}>
                    Certificate of Analysis
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mt: 0.5 }}>
                    {viewTest.product_name} - {viewTest.reference_number}
                  </Typography>
                </Box>
              </Box>
              <IconButton 
                onClick={() => setViewDialogOpen(false)} 
                size="small"
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.grey[400], 0.1),
                    color: 'text.primary'
                  }
                }}
              >
                <Close />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 4 }}>
              <Grid container spacing={4}>
                {/* Product Information */}
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Card sx={{ 
                    borderRadius: 3, 
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    mb: 3,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'default',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 16px 48px ${alpha(theme.palette.success.main, 0.15)}`,
                      border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                    }
                  }}>
                    <CardContent sx={{ p: 4 }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'success.main' }}>
                        Product Information
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                              Product Name
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {viewTest.product_name || 'Not specified'}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                              Brand
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {viewTest.brand_name || 'Not specified'}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                              Batch Number
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {viewTest.batch_number || 'Not specified'}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                              Reference Number
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {viewTest.reference_number || 'Not specified'}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                              Issued Date
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {viewTest.issued_date ? new Date(viewTest.issued_date).toLocaleDateString() : 'Not specified'}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                              Status
                            </Typography>
                            <Chip
                              label={viewTest.coa_status || 'draft'}
                              color={
                                viewTest.coa_status === 'approved' ? 'success' : 
                                viewTest.coa_status === 'issued' ? 'primary' :
                                viewTest.coa_status === 'rejected' ? 'error' : 
                                'default'
                              }
                              size="medium"
                              sx={{
                                fontWeight: 500,
                                textTransform: 'capitalize'
                              }}
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Customer Information */}
                {viewTest.customer_name && (
                  <Grid item xs={12} md={6}>
                    <Card sx={{ 
                      borderRadius: 3, 
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      height: '100%',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'default',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 16px 48px ${alpha(theme.palette.success.main, 0.15)}`,
                        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                      }
                    }}>
                      <CardContent sx={{ p: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'success.main' }}>
                          Customer Information
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                            Customer Name
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                            {viewTest.customer_name}
                          </Typography>
                        </Box>
                        {viewTest.customer_address && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                              Address
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {viewTest.customer_address}
                            </Typography>
                          </Box>
                        )}
                        {viewTest.customer_email && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                              Email
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {viewTest.customer_email}
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Manufacturing Information */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ 
                    borderRadius: 3, 
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    height: '100%',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'default',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 16px 48px ${alpha(theme.palette.success.main, 0.15)}`,
                      border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                    }
                  }}>
                    <CardContent sx={{ p: 4 }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'success.main' }}>
                        Manufacturing Information
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Manufacturing Date
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                          {viewTest.manufacturing_date ? new Date(viewTest.manufacturing_date).toLocaleDateString() : 'Not specified'}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Expiry Date
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {viewTest.expiry_date ? new Date(viewTest.expiry_date).toLocaleDateString() : 'Not specified'}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Lot Size
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {viewTest.lot_size || 'Not specified'}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Physical Testing Results */}
                {(viewTest.appearance || viewTest.color || viewTest.odor || viewTest.ph || viewTest.specific_gravity || viewTest.viscosity || viewTest.density || viewTest.melting_point || viewTest.boiling_point || viewTest.refractive_index) && (
                  <Grid item xs={12}>
                    <Card sx={{ 
                      borderRadius: 3, 
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'default',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 16px 48px ${alpha(theme.palette.success.main, 0.15)}`,
                        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                      }
                    }}>
                      <CardContent sx={{ p: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'success.main' }}>
                          Physical Testing Results
                        </Typography>
                        <Grid container spacing={3}>
                          {viewTest.appearance && (
                            <Grid item xs={12} md={4}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  Appearance
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {viewTest.appearance}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {viewTest.color && (
                            <Grid item xs={12} md={4}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  Color
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {viewTest.color}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {viewTest.odor && (
                            <Grid item xs={12} md={4}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  Odor
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {viewTest.odor}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {viewTest.ph && (
                            <Grid item xs={12} md={4}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  pH
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {viewTest.ph}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {viewTest.specific_gravity && (
                            <Grid item xs={12} md={4}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  Specific Gravity
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {viewTest.specific_gravity}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {viewTest.viscosity && (
                            <Grid item xs={12} md={4}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  Viscosity
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {viewTest.viscosity}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {viewTest.density && (
                            <Grid item xs={12} md={4}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  Density
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {viewTest.density}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {viewTest.melting_point && (
                            <Grid item xs={12} md={4}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  Melting Point
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {viewTest.melting_point}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {viewTest.boiling_point && (
                            <Grid item xs={12} md={4}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  Boiling Point
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {viewTest.boiling_point}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {viewTest.refractive_index && (
                            <Grid item xs={12} md={4}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  Refractive Index
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {viewTest.refractive_index}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Chemical Testing Results */}
                {(viewTest.active_content || viewTest.water_content || viewTest.ash_content || viewTest.heavy_metals || viewTest.preservative_content) && (
                  <Grid item xs={12}>
                    <Card sx={{ 
                      borderRadius: 3, 
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'default',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 16px 48px ${alpha(theme.palette.success.main, 0.15)}`,
                        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                      }
                    }}>
                      <CardContent sx={{ p: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'success.main' }}>
                          Chemical Testing Results
                        </Typography>
                        <Grid container spacing={3}>
                          {viewTest.active_content && (
                            <Grid item xs={12} md={6}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  Active Content
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                                  {viewTest.active_content}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  Method: {viewTest.active_content_method || 'HPLC/GC'}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {viewTest.water_content && (
                            <Grid item xs={12} md={6}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  Water Content
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                                  {viewTest.water_content}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  Method: {viewTest.water_content_method || 'Karl Fischer'}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {viewTest.ash_content && (
                            <Grid item xs={12} md={6}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  Ash Content
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                                  {viewTest.ash_content}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  Method: {viewTest.ash_content_method || 'Gravimetric'}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {viewTest.heavy_metals && (
                            <Grid item xs={12} md={6}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  Heavy Metals
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                                  {viewTest.heavy_metals}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  Method: {viewTest.heavy_metals_method || 'ICP-MS'}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {viewTest.preservative_content && (
                            <Grid item xs={12} md={6}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  Preservative Content
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                                  {viewTest.preservative_content}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  Method: {viewTest.preservative_content_method || 'HPLC'}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Microbiological Testing Results */}
                {(viewTest.total_aerobic_count || viewTest.yeast_mold_count || viewTest.escherichia_coli || viewTest.staphylococcus_aureus || viewTest.pseudomonas_aeruginosa || viewTest.candida_albicans) && (
                  <Grid item xs={12}>
                    <Card sx={{ 
                      borderRadius: 3, 
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'default',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 16px 48px ${alpha(theme.palette.success.main, 0.15)}`,
                        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                      }
                    }}>
                      <CardContent sx={{ p: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'success.main' }}>
                          Microbiological Testing Results
                        </Typography>
                        <Grid container spacing={3}>
                          {viewTest.total_aerobic_count && (
                            <Grid item xs={12} md={6}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  Total Aerobic Count
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                                  {viewTest.total_aerobic_count}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  Limit: {viewTest.total_aerobic_count_limit || '≤1000 CFU/g'}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {viewTest.yeast_mold_count && (
                            <Grid item xs={12} md={6}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  Yeast & Mold Count
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                                  {viewTest.yeast_mold_count}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  Limit: {viewTest.yeast_mold_count_limit || '≤100 CFU/g'}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {viewTest.escherichia_coli && (
                            <Grid item xs={12} md={6}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  E. coli
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                                  {viewTest.escherichia_coli}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  Limit: {viewTest.escherichia_coli_limit || 'Not Detected'}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {viewTest.staphylococcus_aureus && (
                            <Grid item xs={12} md={6}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  S. aureus
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                                  {viewTest.staphylococcus_aureus}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  Limit: {viewTest.staphylococcus_aureus_limit || 'Not Detected'}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {viewTest.pseudomonas_aeruginosa && (
                            <Grid item xs={12} md={6}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  P. aeruginosa
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                                  {viewTest.pseudomonas_aeruginosa}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  Limit: {viewTest.pseudomonas_aeruginosa_limit || 'Not Detected'}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {viewTest.candida_albicans && (
                            <Grid item xs={12} md={6}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  Candida albicans
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                                  {viewTest.candida_albicans}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  Limit: {viewTest.candida_albicans_limit || 'Not Detected'}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Regulatory & Compliance */}
                {(viewTest.compliance_standards && viewTest.compliance_standards.length > 0) || viewTest.gmo_status || viewTest.cruelty_free || viewTest.vegan_status && (
                  <Grid item xs={12}>
                    <Card sx={{ 
                      borderRadius: 3, 
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'default',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 16px 48px ${alpha(theme.palette.success.main, 0.15)}`,
                        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                      }
                    }}>
                      <CardContent sx={{ p: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'success.main' }}>
                          Regulatory & Compliance
                        </Typography>
                        <Grid container spacing={3}>
                          {viewTest.compliance_standards && viewTest.compliance_standards.length > 0 && (
                            <Grid item xs={12}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 1 }}>
                                  Compliance Standards
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                  {viewTest.compliance_standards.map((standard, index) => (
                                    <Chip 
                                      key={index}
                                      label={standard} 
                                      size="small" 
                                      variant="outlined"
                                      sx={{ 
                                        fontWeight: 500,
                                        borderColor: theme.palette.success.main,
                                        color: theme.palette.success.main
                                      }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            </Grid>
                          )}
                          {viewTest.gmo_status && (
                            <Grid item xs={12} md={4}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  GMO Status
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>
                                  {viewTest.gmo_status.replace('-', ' ')}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {viewTest.cruelty_free && (
                            <Grid item xs={12} md={4}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  Cruelty Free
                                </Typography>
                                <Chip 
                                  label="CRUELTY FREE" 
                                  size="small" 
                                  color="success"
                                  sx={{ fontWeight: 500 }}
                                />
                              </Box>
                            </Grid>
                          )}
                          {viewTest.vegan_status && (
                            <Grid item xs={12} md={4}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  Vegan Status
                                </Typography>
                                <Chip 
                                  label="VEGAN" 
                                  size="small" 
                                  color="success"
                                  sx={{ fontWeight: 500 }}
                                />
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Additional Information */}
                {(viewTest.ingredient_disclosure || viewTest.safety_warnings || viewTest.shelf_life || viewTest.country_of_origin || viewTest.additional_notes) && (
                  <Grid item xs={12}>
                    <Card sx={{ 
                      borderRadius: 3, 
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'default',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 16px 48px ${alpha(theme.palette.success.main, 0.15)}`,
                        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                      }
                    }}>
                      <CardContent sx={{ p: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'success.main' }}>
                          Additional Information
                        </Typography>
                        <Grid container spacing={3}>
                          {viewTest.shelf_life && (
                            <Grid item xs={12} md={6}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  Shelf Life
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {viewTest.shelf_life}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {viewTest.country_of_origin && (
                            <Grid item xs={12} md={6}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  Country of Origin
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {viewTest.country_of_origin}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {viewTest.ingredient_disclosure && (
                            <Grid item xs={12}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  Ingredient Disclosure
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {viewTest.ingredient_disclosure}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {viewTest.safety_warnings && (
                            <Grid item xs={12}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  Safety Warnings
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {viewTest.safety_warnings}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {viewTest.additional_notes && (
                            <Grid item xs={12}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  Additional Notes
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {viewTest.additional_notes}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                                 {/* Quality Control */}
                 {(viewTest.qc_manager || viewTest.qc_technician || viewTest.test_method) && (
                   <Grid item xs={12}>
                     <Card sx={{ 
                       borderRadius: 3, 
                       border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                       background: 'white',
                       transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                       cursor: 'default',
                       '&:hover': {
                         transform: 'translateY(-2px)',
                         boxShadow: `0 16px 48px ${alpha(theme.palette.success.main, 0.15)}`,
                         border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                       }
                     }}>
                       <CardContent sx={{ p: 4 }}>
                         <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'success.main' }}>
                           Quality Control
                         </Typography>
                         <Grid container spacing={3}>
                           {viewTest.qc_manager && (
                             <Grid item xs={12} md={6}>
                               <Box sx={{ mb: 2 }}>
                                 <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                   QC Manager
                                 </Typography>
                                 <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                   {viewTest.qc_manager}
                                 </Typography>
                               </Box>
                             </Grid>
                           )}
                           {viewTest.qc_technician && (
                             <Grid item xs={12} md={6}>
                               <Box sx={{ mb: 2 }}>
                                 <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                   QC Technician
                                 </Typography>
                                 <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                   {viewTest.qc_technician}
                                 </Typography>
                               </Box>
                             </Grid>
                           )}
                           {viewTest.test_method && (
                             <Grid item xs={12}>
                               <Box sx={{ mb: 2 }}>
                                 <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                   Test Method
                                 </Typography>
                                 <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                   {viewTest.test_method}
                                 </Typography>
                               </Box>
                             </Grid>
                           )}
                         </Grid>
                       </CardContent>
                     </Card>
                   </Grid>
                 )}

                {/* Signature */}
                {viewTest.signature_data && (
                  <Grid item xs={12}>
                    <Card sx={{ 
                      borderRadius: 3, 
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'default',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 16px 48px ${alpha(theme.palette.success.main, 0.15)}`,
                        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                      }
                    }}>
                      <CardContent sx={{ p: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'success.main' }}>
                          Authorized Signature
                        </Typography>
                        <Box sx={{ 
                          fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                          fontSize: '32px', 
                          color: '#000000',
                          mb: 2,
                          textShadow: '1px 1px 1px rgba(0,0,0,0.1)'
                        }}>
                          {viewTest.signature_data.name}
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                          {viewTest.signature_data.position || 'Quality Control Manager'}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary', mt: 1 }}>
                          Signed on {new Date(viewTest.signature_data.date).toLocaleDateString()}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            
            <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`, bgcolor: 'white' }}>
              <Button 
                onClick={() => setViewDialogOpen(false)}
                sx={{ 
                  borderRadius: 2,
                  fontWeight: 500,
                  color: 'text.secondary'
                }}
              >
                Close
              </Button>
              {canEdit && (
                <Button 
                  variant="outlined"
                  onClick={() => {
                    setViewDialogOpen(false);
                    handleCoADialogOpen(viewTest);
                  }}
                  startIcon={<Edit />}
                  sx={{ 
                    borderRadius: 2,
                    fontWeight: 500,
                    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                    color: 'success.main',
                    '&:hover': {
                      border: `1px solid ${theme.palette.success.main}`,
                      bgcolor: alpha(theme.palette.success.main, 0.05)
                    }
                  }}
                >
                  Edit Certificate
                </Button>
              )}
              <Button 
                variant="contained"
                onClick={() => handlePrintCoA(viewTest)}
                startIcon={<Print />}
                sx={{ 
                  borderRadius: 2,
                  fontWeight: 500,
                  background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`
                  }
                }}
              >
                Print Certificate
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default CertificateOfAnalysis;
